// Proper Cognito authentication using AWS SDK v3
// This uses InitiateAuth flow which works from the browser without AWS credentials

console.log('🔍 CognitoAuthService file loaded');

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { loadCognitoConfig as loadCognitoConfigFromFile } from '../config/cognito';

// Cognito configuration - will be loaded dynamically
let cognitoConfig: {
  userPoolId: string;
  userPoolClientId: string;
  region: string;
} | null = null;

// Load Cognito config from window object or reload from config.json if needed
async function ensureCognitoConfig() {
  // Check if config is already loaded and valid
  const windowConfig = (window as any).__COGNITO_CONFIG;
  if (windowConfig && windowConfig.userPoolId && windowConfig.userPoolClientId) {
    cognitoConfig = windowConfig;
    cognitoClient = new CognitoIdentityProviderClient({
      region: windowConfig.region || 'us-east-1',
    });
    return;
  }

  // If not valid, reload from config.json
  await loadCognitoConfigFromFile();
  
  // Check again after reload
  const reloadedConfig = (window as any).__COGNITO_CONFIG;
  if (!reloadedConfig || !reloadedConfig.userPoolId || !reloadedConfig.userPoolClientId) {
    // Config not available - set to null instead of throwing
    // The login function will check and show a user-friendly error
    cognitoConfig = null;
    return;
  }
  
  cognitoConfig = reloadedConfig;
  cognitoClient = new CognitoIdentityProviderClient({
    region: reloadedConfig.region || 'us-east-1',
  });
}

// Create Cognito client (no credentials needed for InitiateAuth)
let cognitoClient: CognitoIdentityProviderClient;

export interface LoginResult {
  success: boolean;
  challengeName?: string;
  session?: string;
  user?: any;
  error?: string;
  userType?: 'admin' | 'standard' | 'super';
  redirectUrl?: string;
}

export interface UserDetails {
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
  companyName?: string;
  roleId?: string;
  role?: string;
  userType?: 'admin' | 'standard' | 'super';
  setupCompleted?: boolean;
  isServiceAccount?: boolean;
}

export class CognitoAuthService {
  /**
   * Authenticate user with username and password using InitiateAuth
   * This works from the browser without AWS credentials
   */
  static async login(username: string, password: string): Promise<LoginResult> {
    try {
      // Load dynamic config before using
      await ensureCognitoConfig();

      if (!cognitoConfig || !cognitoConfig.userPoolId || !cognitoConfig.userPoolClientId) {
        throw new Error('Cognito configuration not loaded');
      }

      const command = new InitiateAuthCommand({
        ClientId: cognitoConfig.userPoolClientId,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);
      console.log('📡 Cognito login response:', response);
      console.log('🔍 Response details:', {
        ChallengeName: response.ChallengeName,
        AuthenticationResult: response.AuthenticationResult,
        Session: response.Session,
        ChallengeParameters: response.ChallengeParameters,
      });

      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: response.Session,
          user: { username },
        };
      }

      if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        return {
          success: false,
          challengeName: 'SOFTWARE_TOKEN_MFA',
          session: response.Session,
          user: { username },
        };
      }

      if (response.AuthenticationResult) {
        // Login successful - store tokens
        this.storeTokens(response.AuthenticationResult);

        // Get user details to determine user type
        if (response.AuthenticationResult.AccessToken) {
          const userDetails = await this.getUserDetails(response.AuthenticationResult.AccessToken);

          // Check if user is a service account - deny access
          if (userDetails.isServiceAccount) {
            return {
              success: false,
              user: { ...userDetails, username },
              userType: 'standard',
              error: 'Service accounts cannot access the web portal',
            };
          }

          // Determine user type and redirect accordingly
          const userType = this.determineUserType(userDetails);

          // All non-service accounts get access to the web portal
          return {
            success: true,
            user: { ...userDetails, username },
            userType,
          };
        }

        return {
          success: true,
          user: { username },
          userType: 'admin', // Default to admin if we can't determine
        };
      }

      console.log('❌ Unexpected Cognito response structure:', {
        ChallengeName: response.ChallengeName,
        AuthenticationResult: response.AuthenticationResult,
        Session: response.Session,
        ChallengeParameters: response.ChallengeParameters,
      });

      return {
        success: false,
        error: 'Unexpected response from Cognito',
      };
    } catch (error: any) {
      console.error('❌ Cognito login error:', error);
      console.error('   Error name:', error.name);
      console.error('   Error message:', error.message);
      if (error.$metadata) {
        console.error('   Request ID:', error.$metadata.requestId);
        console.error('   HTTP Status:', error.$metadata.httpStatusCode);
      }

      // Handle specific Cognito errors with detailed messages
      if (error.name === 'NotAuthorizedException') {
        return {
          success: false,
          error: 'Invalid username or password. Please check your credentials and try again.',
        };
      }

      if (error.name === 'UserNotFoundException') {
        return {
          success: false,
          error: 'User not found. Please check your username or contact your administrator.',
        };
      }

      if (error.name === 'UserNotConfirmedException') {
        return {
          success: false,
          error: 'Your account is not confirmed. Please check your email for a confirmation link or contact your administrator.',
        };
      }

      if (error.name === 'InvalidParameterException') {
        return {
          success: false,
          error: `Configuration error: ${error.message || 'Invalid parameters provided'}. Please contact support.`,
        };
      }

      if (error.name === 'InvalidPasswordException') {
        return {
          success: false,
          error: 'Invalid password format. Please check your password and try again.',
        };
      }

      if (error.name === 'PasswordResetRequiredException') {
        return {
          success: false,
          error: 'Password reset required. Please reset your password before logging in.',
        };
      }

      if (error.name === 'UserLambdaValidationException') {
        return {
          success: false,
          error: `Authentication validation failed: ${error.message || 'Please contact support'}.`,
        };
      }

      if (error.name === 'TooManyRequestsException') {
        return {
          success: false,
          error: 'Too many login attempts. Please wait a few minutes and try again.',
        };
      }

      if (error.name === 'LimitExceededException') {
        return {
          success: false,
          error: 'Login limit exceeded. Please wait a few minutes and try again.',
        };
      }

      if (error.name === 'ResourceNotFoundException') {
        return {
          success: false,
          error: 'Authentication service not found. Please contact your administrator.',
        };
      }

      if (error.name === 'InternalErrorException') {
        return {
          success: false,
          error: 'An internal error occurred. Please try again in a few moments.',
        };
      }

      // Check for "Exhausted available authentication methods" error
      const errorMessage = error.message || '';
      if (
        errorMessage.includes('Exhausted available authentication methods') ||
        errorMessage.includes('exhausted available authentication methods')
      ) {
        return {
          success: false,
          error: 'Authentication method not available. This may be due to: (1) User account not confirmed, (2) Missing authentication flow configuration, or (3) Account disabled. Please contact your administrator.',
        };
      }

      // Generic error with more details
      return {
        success: false,
        error: error.message || 'Login failed. Please try again or contact support if the problem persists.',
      };
    }
  }

  /**
   * Respond to MFA challenge
   */
  static async respondToMfaChallenge(
    session: string,
    mfaCode: string,
    username: string
  ): Promise<LoginResult> {
    try {
      console.log('🔍 Responding to MFA challenge');
      console.log('🔍 Session:', session ? 'Present' : 'Missing');
      console.log('🔍 MFA Code length:', mfaCode?.length);
      console.log('🔍 Username:', username);

      await ensureCognitoConfig();

      if (!cognitoConfig || !cognitoConfig.userPoolId || !cognitoConfig.userPoolClientId) {
        throw new Error('Cognito configuration not loaded');
      }

      const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.userPoolClientId,
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        Session: session,
        ChallengeResponses: {
          SOFTWARE_TOKEN_MFA_CODE: mfaCode,
          USERNAME: username,
        },
      });

      const response = await cognitoClient.send(command);
      console.log('📡 MFA challenge response:', response);

      if (response.AuthenticationResult) {
        // MFA successful - store tokens
        this.storeTokens(response.AuthenticationResult);

        // Get user details to determine user type
        if (response.AuthenticationResult.AccessToken) {
          const userDetails = await this.getUserDetails(response.AuthenticationResult.AccessToken);

          // Check if user is a service account - deny access
          if (userDetails.isServiceAccount) {
            return {
              success: false,
              user: { ...userDetails },
              userType: 'standard',
              error: 'Service accounts cannot access the web portal',
            };
          }

          // Determine user type and redirect accordingly
          const userType = this.determineUserType(userDetails);

          return {
            success: true,
            user: { ...userDetails },
            userType,
          };
        }

        return {
          success: true,
          user: { username: 'unknown' },
          userType: 'admin', // Default to admin if we can't determine
        };
      }

      return {
        success: false,
        error: 'MFA verification failed',
      };
    } catch (error: any) {
      console.error('❌ MFA challenge error:', error);
      console.error('   Error name:', error.name);
      console.error('   Error message:', error.message);
      if (error.$metadata) {
        console.error('   Request ID:', error.$metadata.requestId);
        console.error('   HTTP Status:', error.$metadata.httpStatusCode);
      }

      if (error.name === 'CodeMismatchException') {
        return {
          success: false,
          error: 'Invalid MFA code. Please check your authenticator app and try again.',
        };
      }

      if (error.name === 'ExpiredCodeException') {
        return {
          success: false,
          error: 'MFA code has expired. Please generate a new code and try again.',
        };
      }

      if (error.name === 'NotAuthorizedException') {
        return {
          success: false,
          error: 'MFA session expired. Please log in again.',
        };
      }

      if (error.name === 'InvalidParameterException') {
        return {
          success: false,
          error: `MFA configuration error: ${error.message || 'Invalid parameters'}. Please contact support.`,
        };
      }

      if (error.name === 'TooManyRequestsException') {
        return {
          success: false,
          error: 'Too many MFA attempts. Please wait a few minutes and try again.',
        };
      }

      if (error.name === 'LimitExceededException') {
        return {
          success: false,
          error: 'MFA verification limit exceeded. Please wait a few minutes and try again.',
        };
      }

      return {
        success: false,
        error: error.message || 'MFA verification failed. Please try again or contact support.',
      };
    }
  }

  /**
   * Get user details from Cognito using access token
   */
  static async getUserDetails(accessToken: string): Promise<UserDetails> {
    // Ensure Cognito config is loaded
    if (!cognitoClient || !cognitoConfig) {
      await ensureCognitoConfig();
    }

    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      });

      const response = await cognitoClient.send(command);

      const userDetails: UserDetails = {
        username: response.Username || '',
      };

      // Extract custom attributes
      if (response.UserAttributes) {
        response.UserAttributes.forEach(attr => {
          switch (attr.Name) {
            case 'email':
              userDetails.email = attr.Value;
              break;
            case 'custom:firstName':
              userDetails.firstName = attr.Value;
              break;
            case 'custom:lastName':
              userDetails.lastName = attr.Value;
              break;
            case 'custom:companyId':
              userDetails.companyId = attr.Value;
              break;
            case 'custom:companyName':
              userDetails.companyName = attr.Value;
              break;
            case 'custom:roleId':
              userDetails.roleId = attr.Value;
              break;
            case 'custom:role':
              userDetails.role = attr.Value;
              break;
            case 'custom:setupCompleted':
              userDetails.setupCompleted = attr.Value === 'true';
              break;
            case 'custom:isServiceAccount':
              userDetails.isServiceAccount = attr.Value === 'true';
              break;
          }
        });
      }

      // Determine if user is a service account based on username pattern
      if (!userDetails.isServiceAccount) {
        userDetails.isServiceAccount = userDetails.username.startsWith('SVC_');
      }

      return userDetails;
    } catch (error) {
      console.error('❌ Error getting user details:', error);
      return { username: '' };
    }
  }

  /**
   * Determine user type based on role or other attributes
   */
  static determineUserType(userDetails: UserDetails): 'admin' | 'standard' | 'super' {
    // Logic to determine user type based on role or company
    // This is a simplified example - you can enhance this logic

    console.log('🔍 Determining user type for:', {
      username: userDetails.username,
      role: userDetails.role,
      roleId: userDetails.roleId,
    });

    // Check if user is bootstrap admin
    if (userDetails.username === 'bootstrap-admin') {
      console.log('✅ User determined as admin (bootstrap)');
      return 'admin';
    }

    // Check role from Cognito attributes or database
    const userRole = userDetails.role || userDetails.roleId;

    if (userRole === 'admin') {
      console.log('✅ User determined as admin');
      return 'admin';
    }

    if (userRole === 'super-user') {
      console.log('✅ User determined as super');
      return 'super';
    }

    // Default to standard user - role will be fetched from database when needed
    console.log('✅ User determined as standard (default)');
    return 'standard';
  }

  /**
   * Check if user needs to complete initial setup
   * This could be based on custom attributes, first login, etc.
   */
  static needsInitialSetup(userDetails: UserDetails): boolean {
    // Check if user has completed initial setup
    // This could be based on a custom attribute like 'custom:setupCompleted'
    // For now, we'll assume all users need initial setup unless they have a specific attribute

    // You can customize this logic based on your requirements
    // For example, check if custom:setupCompleted is 'true'
    // For demo purposes, we'll check if the user has a specific attribute
    return !userDetails.setupCompleted; // Check for setupCompleted attribute
  }

  /**
   * Store authentication tokens in localStorage
   */
  static storeTokens(authResult: any) {
    if (authResult.AccessToken) {
      localStorage.setItem('cognito_access_token', authResult.AccessToken);
    }
    if (authResult.RefreshToken) {
      localStorage.setItem('cognito_refresh_token', authResult.RefreshToken);
    }
    if (authResult.IdToken) {
      localStorage.setItem('cognito_id_token', authResult.IdToken);
    }
  }

  /**
   * Clear stored tokens
   */
  static clearTokens() {
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_id_token');
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!localStorage.getItem('cognito_access_token');
  }

  /**
   * Get stored access token
   */
  static getAccessToken(): string | null {
    return localStorage.getItem('cognito_access_token');
  }

  /**
   * Logout user
   */
  static logout() {
    this.clearTokens();
    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Initiate password reset for standard/super users
   */
  static async forgotPassword(username: string): Promise<{ success: boolean; error?: string }> {
    try {
      await ensureCognitoConfig();
      
      if (!cognitoConfig || !cognitoConfig.userPoolClientId) {
        throw new Error('Cognito configuration not loaded');
      }

      const command = new ForgotPasswordCommand({
        ClientId: cognitoConfig.userPoolClientId,
        Username: username,
      });

      await cognitoClient.send(command);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      return {
        success: false,
        error: error.message || 'Failed to initiate password reset',
      };
    }
  }

  /**
   * Confirm password reset with verification code
   */
  static async confirmForgotPassword(
    username: string,
    verificationCode: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await ensureCognitoConfig();
      
      if (!cognitoConfig || !cognitoConfig.userPoolClientId) {
        throw new Error('Cognito configuration not loaded');
      }

      const command = new ConfirmForgotPasswordCommand({
        ClientId: cognitoConfig.userPoolClientId,
        Username: username,
        ConfirmationCode: verificationCode,
        Password: newPassword,
      });

      await cognitoClient.send(command);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Password confirmation error:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm password reset',
      };
    }
  }

  /**
   * Respond to NEW_PASSWORD_REQUIRED challenge
   */
  static async respondToNewPasswordChallenge(
    username: string,
    newPassword: string,
    session: string
  ): Promise<LoginResult> {
    try {
      await ensureCognitoConfig();
      
      if (!cognitoConfig || !cognitoConfig.userPoolClientId) {
        throw new Error('Cognito configuration not loaded');
      }

      const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.userPoolClientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
      });

      const response = await cognitoClient.send(command);
      console.log('📡 Password challenge response received successfully');

      if (response.AuthenticationResult) {
        // Password change successful - store tokens
        this.storeTokens(response.AuthenticationResult);

        // Get user details
        if (response.AuthenticationResult.AccessToken) {
          const userDetails = await this.getUserDetails(response.AuthenticationResult.AccessToken);
          const userType = this.determineUserType(userDetails);

          return {
            success: true,
            user: { ...userDetails, username },
            userType,
          };
        }
      }

      return {
        success: false,
        error: 'Password change failed - no authentication result',
      };
    } catch (error: any) {
      console.error('❌ Password challenge error:', error);
      return {
        success: false,
        error: error.message || 'Failed to change password',
      };
    }
  }
}
